import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Search } from 'lucide-react';
import { Stat } from '@/shared/ui';
import type { OverviewFindability, OverviewFlags } from '../api/overviewApi';
import { LockedSection } from './LockedSection';
import styles from './FindabilitySection.module.css';

interface FindabilitySectionProps {
  flags: OverviewFlags;
  data: OverviewFindability;
}

export function FindabilitySection({ flags, data }: FindabilitySectionProps) {
  const navigate = useNavigate();

  if (!flags.hasSEO) {
    return (
      <LockedSection
        icon={Search}
        ctaColor="purple"
        headline="Connect Google Search Console & Business Profile to see how patients find you"
        sub="Track search clicks, keyword trends, AI search readiness, and your Online Visibility Score — all in one place."
        cta="Connect GSC & GBP →"
        onCtaClick={() => navigate('/settings/organic')}
      />
    );
  }

  const readinessLabel = data.aeoReadiness
    ? data.aeoReadiness.charAt(0).toUpperCase() + data.aeoReadiness.slice(1)
    : null;
  const readinessIsHigh = data.aeoReadiness === 'high';

  return (
    <div className={styles.grid}>
      {/* Search clicks */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <Stat
            label="Google Search Clicks"
            value={data.clicks !== null ? data.clicks.toLocaleString() : '—'}
            size="lg"
            sub={
              data.clicksTrendPct !== null ? (
                <span
                  className={clsx(
                    styles.delta,
                    data.clicksTrendPct >= 0 ? styles.deltaUp : styles.deltaDown,
                  )}
                >
                  {data.clicksTrendPct >= 0 ? '+' : ''}
                  {data.clicksTrendPct}% vs last month
                </span>
              ) : (
                <span className={styles.deltaMuted}>Collecting data…</span>
              )
            }
          />
        </div>
        <div className={styles.hint}>Last 28 days · from Google Search Console</div>
      </div>

      {/* AI readiness */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <Stat
            label="AI Search Readiness"
            value={readinessLabel ?? '—'}
            size="lg"
            tone={readinessIsHigh ? 'accent' : 'warning'}
            sub="ChatGPT, Gemini"
          />
        </div>
        {/* S1.3: "x of N signals" shows nothing when there are no real checks — no made-up 14,
            no guessed evaluating-copy either. */}
        {data.aeoSignalsPassed !== null && data.aeoSignalsTotal !== null && (
          <div className={styles.hint}>
            {`${data.aeoSignalsPassed} of ${data.aeoSignalsTotal} signals in place`}
          </div>
        )}
      </div>

      {/* Visibility score */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <Stat
            label="Online Visibility Score"
            value={
              <>
                {data.visibilityScore ?? '—'}
                <span className={styles.scoreDenom}>/100</span>
              </>
            }
            size="lg"
            tone={(data.visibilityScore ?? 0) >= 75 ? 'accent' : 'warning'}
            sub={
              data.visibilityDelta !== null ? (
                <span
                  className={clsx(
                    styles.delta,
                    data.visibilityDelta >= 0 ? styles.deltaUp : styles.deltaWarning,
                  )}
                >
                  {data.visibilityDelta >= 0 ? '+' : ''}
                  {data.visibilityDelta} pts
                </span>
              ) : undefined
            }
          />
          {data.visibilityGrade && (
            <div
              className={clsx(
                styles.gradeBadge,
                (data.visibilityScore ?? 0) >= 75 ? styles.gradeAccent : styles.gradeWarning,
              )}
            >
              Grade {data.visibilityGrade}
            </div>
          )}
        </div>
        <button type="button" className={styles.link} onClick={() => navigate('/seo')}>
          View full breakdown →
        </button>
      </div>
    </div>
  );
}
